"use client"

import { useRouter } from "next/navigation"

import { useScrollYThreshold } from "@/hooks/useScrollYThreshold"
import { useCallback } from "react"
import { IconButton } from "../button/IconButton"
import { programmaticFocus } from "@/utils/programmaticFocus"

export type NavigationProps = {
  title: string
  left?: React.ReactNode
  right?: React.ReactNode
  isScrolled?: boolean
  style?: React.CSSProperties
}

export const NaviBackItem = ({ color }: { color?: string }) => {
  const router = useRouter()
  const handleBack = useCallback(() => {
    router.back()
  }, [router])
  return (
    <IconButton
      src="/images/icon/back_24.webp"
      color={color ? color : `bg-rl-gray-900`}
      iconSize={24}
      ariaLabel="뒤로가기"
      priority={true}
      onClick={handleBack}
    />
  )
}

export const NaviCloseItem = ({ color }: { color?: string }) => {
  const router = useRouter()
  const handleBack = useCallback(() => {
    router.back()
  }, [router])
  return (
    <IconButton
      src="/images/icon/close_24.webp"
      color={color ? color : `bg-rl-gray-900`}
      iconSize={24}
      ariaLabel="닫기"
      priority={true}
      onClick={handleBack}
    />
  )
}

export const NaviHomeItem = ({ color }: { color?: string }) => {
  const router = useRouter()
  const handleBack = useCallback(() => {
    router.replace("/")
  }, [router])
  return (
    <IconButton
      src="/images/icon/home_24.webp"
      color={color ? color : `bg-rl-gray-900`}
      iconSize={24}
      ariaLabel="홈으로 가기"
      priority={true}
      onClick={handleBack}
    />
  )
}

// 초기 페이지 로드 완료 후 false로 전환.
let _isInitialLoad = true
if (typeof window !== "undefined") {
  setTimeout(() => {
    _isInitialLoad = false
  }, 0)
}

/**
 * SPA 네비게이션 후 첫 포커서블 요소에 자동 포커스.
 * 초기 페이지 로드 시에는 포커스하지 않음 (_isInitialLoad 플래그).
 * 포커스 링은 CSS :focus:not(:focus-visible) 규칙으로 비표시.
 */
export const navAutoFocusRef = (node: HTMLElement | null) => {
  if (!node || _isInitialLoad) return
  programmaticFocus(node.querySelector<HTMLElement>("button, a, [tabindex]"))
}

const NavigationBar = ({
  title,
  left = <NaviBackItem />,
  right,
  isScrolled,
  bgColor = "",
  style,
}: NavigationProps & { bgColor?: string }) => {
  const _isScrolled = isScrolled ?? true

  return (
    <div
      ref={navAutoFocusRef}
      className={`sticky top-0 z-100 ${_isScrolled ? "bg-rl-common-white shadow-rl-normal" : ""} navibar-height w-full ${bgColor}`}
      style={style}
    >
      <div className="absolute left-4 top-1/2 flex -translate-y-1/2 items-center gap-4">
        {left}
        {title && (
          <span
            tabIndex={0}
            className="typo-rl-body1-title text-rl-neutral-900 font-bold"
          >
            {title}
          </span>
        )}
      </div>
      <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-4">
        {right}
      </div>
    </div>
  )
}

export const StickyNavigaionBar = ({
  title,
  left,
  right,
  bgColor = "bg-rl-common-white",
  style,
}: {
  title: string
  left?: React.ReactNode
  right?: React.ReactNode
  bgColor?: string
  style?: React.CSSProperties
}) => {
  const isScrolled = useScrollYThreshold(20)
  return (
    <NavigationBar
      title={title}
      left={left}
      right={right}
      isScrolled={isScrolled}
      bgColor={bgColor}
      style={style}
    />
  )
}

export default NavigationBar

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dummy = () => <div className="-h-12"></div>
